(() => {
  'use strict';

  if (customElements.get('ab-search')) return;

  const DEBOUNCE_MS = 220;

  const MIN_QUERY_LENGTH = 2;

  const PRODUCT_LIMIT = 6;
  const QUERY_LIMIT = 4;
  const COLLECTION_LIMIT = 4;

  const SELECTOR = Object.freeze({
    form: '[data-ab-search-form]',
    input: '[data-ab-search-input]',
    clear: '[data-ab-search-clear]',
    panel: '[data-ab-search-panel]',
    option: '[data-ab-search-option]',
  });

  const STATE_CLASS = Object.freeze({
    OPEN: 'is-open',
    ACTIVE: 'is-active',
    LOADING: 'is-loading',
  });

  function createElement(tagName, options = {}) {
    const element = document.createElement(tagName);
    if (options.className) element.className = options.className;
    if (options.text != null) element.textContent = options.text;
    Object.entries(options.attributes || {}).forEach(([name, value]) => {
      if (value != null) element.setAttribute(name, String(value));
    });
    return element;
  }

  function highlightMatch(title, term) {
    const fragment = document.createDocumentFragment();
    const haystack = title.toLowerCase();
    const needle = term.trim().toLowerCase();
    const at = needle ? haystack.indexOf(needle) : -1;

    if (at === -1) {
      fragment.appendChild(document.createTextNode(title));
      return fragment;
    }

    fragment.appendChild(document.createTextNode(title.slice(0, at)));
    fragment.appendChild(createElement('mark', { text: title.slice(at, at + needle.length) }));
    fragment.appendChild(document.createTextNode(title.slice(at + needle.length)));
    return fragment;
  }

  function formatMoney(amount) {
    const major = parseFloat(amount);
    if (!isFinite(major)) return '';

    const cents = Math.round(major * 100);
    const format = (document.body.dataset.moneyFormat || '').trim();

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && format) {
      try {
        return window.Shopify.formatMoney(cents, format).trim();
      } catch (error) {
        return major.toFixed(2);
      }
    }

    return major.toFixed(2);
  }

  function searchRoot() {
    const routes = window.Shopify && window.Shopify.routes;
    const root = routes && routes.root ? routes.root.replace(/\/$/, '') : '';
    return `${root}/search`;
  }

  // Collections as chips. Shared by the dropdown and the results page.
  function buildCollectionGroup(collections, term, options = {}) {
    const group = createElement('div', { className: options.className || 'ab-search__group' });
    group.appendChild(createElement('p', { className: options.titleClassName || 'ab-search__group-title', text: options.title || 'Collections' }));

    const chips = createElement('div', { className: options.chipsClassName || 'ab-search__chips' });

    collections.forEach((entry) => {
      const chip = createElement('a', {
        className: options.chipClassName || 'ab-search__chip',
        attributes: {
          href: entry.url,
          'data-ab-search-option': options.forPage ? null : '',
          'data-no-instant': '',
          role: options.forPage ? null : 'option',
        },
      });
      chip.appendChild(highlightMatch(entry.title || '', term));
      chips.appendChild(chip);
    });

    group.appendChild(chips);
    return group;
  }

  class AbSearch extends HTMLElement {
    constructor() {
      super();
      this.handleInput = this.handleInput.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleFocus = this.handleFocus.bind(this);
      this.handleClear = this.handleClear.bind(this);
      this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.handlePanelPointerOver = this.handlePanelPointerOver.bind(this);
    }

    connectedCallback() {
      this.elements = {
        form: this.querySelector(SELECTOR.form),
        input: this.querySelector(SELECTOR.input),
        clear: this.querySelector(SELECTOR.clear),
        panel: this.querySelector(SELECTOR.panel),
      };

      if (!this.elements.input || !this.elements.panel) return;

      this.activeIndex = -1;
      this.renderedQuery = '';
      this.debounceTimer = null;
      this.abortController = null;

      this.elements.input.addEventListener('input', this.handleInput);
      this.elements.input.addEventListener('keydown', this.handleKeyDown);
      this.elements.input.addEventListener('focus', this.handleFocus);
      this.elements.panel.addEventListener('pointerover', this.handlePanelPointerOver);
      if (this.elements.clear) this.elements.clear.addEventListener('click', this.handleClear);

      document.addEventListener('pointerdown', this.handleDocumentPointerDown);

      this.syncClearButton();
    }

    disconnectedCallback() {
      window.clearTimeout(this.debounceTimer);
      if (this.abortController) this.abortController.abort();
      document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
    }

    handleInput() {
      this.syncClearButton();
      const query = this.elements.input.value.trim();

      window.clearTimeout(this.debounceTimer);

      if (query.length < MIN_QUERY_LENGTH) {
        this.close();
        return;
      }

      this.debounceTimer = window.setTimeout(() => this.fetchSuggestions(query), DEBOUNCE_MS);
    }

    handleFocus() {
      const query = this.elements.input.value.trim();
      if (query.length >= MIN_QUERY_LENGTH && query === this.renderedQuery) this.open();
    }

    handleClear() {
      this.elements.input.value = '';
      this.renderedQuery = '';
      this.syncClearButton();
      this.close();
      this.elements.input.focus();
    }

    handleKeyDown(event) {
      const options = this.optionElements();

      switch (event.key) {
        case 'ArrowDown':
          if (!options.length) return;
          event.preventDefault();
          this.setActiveIndex(this.activeIndex + 1 >= options.length ? 0 : this.activeIndex + 1);
          break;

        case 'ArrowUp':
          if (!options.length) return;
          event.preventDefault();
          this.setActiveIndex(this.activeIndex - 1 < 0 ? options.length - 1 : this.activeIndex - 1);
          break;

        case 'Enter':
          if (this.activeIndex > -1 && options[this.activeIndex]) {
            event.preventDefault();
            options[this.activeIndex].click();
          } else if (this.elements.form && this.elements.input.value.trim()) {
            // The theme cancels Enter on every input (checkout_clone handler), so submit the search directly.
            event.preventDefault();
            event.stopPropagation();
            if (typeof this.elements.form.requestSubmit === 'function') this.elements.form.requestSubmit();
            else this.elements.form.submit();
          }
          break;

        case 'Escape':
          this.close();
          this.elements.input.blur();
          break;

        default:
          break;
      }
    }

    handlePanelPointerOver(event) {
      const option = event.target.closest(SELECTOR.option);
      if (!option) return;
      this.setActiveIndex(this.optionElements().indexOf(option));
    }

    handleDocumentPointerDown(event) {
      if (!this.contains(event.target)) this.close();
    }

    async fetchSuggestions(query) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this.setLoading(true);

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,query,collection',
        'resources[limit]': String(PRODUCT_LIMIT),
        // Limit per type, not shared across types, so collections don't push products out.
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor',
      });

      try {
        const response = await fetch(`${this.searchUrl()}/suggest.json?${params}`, {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

        const body = await response.json();
        this.renderResults(query, body.resources ? body.resources.results : {});
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[ab-search]', error);
        this.renderMessage('Search is unavailable right now. Press Enter to see all results.');
      } finally {
        if (!this.abortController.signal.aborted) this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.classList.toggle(STATE_CLASS.LOADING, isLoading);
    }

    searchUrl() {
      const routes = window.Shopify && window.Shopify.routes;
      const root = routes && routes.root ? routes.root.replace(/\/$/, '') : '';
      return `${root}/search`;
    }

    renderMessage(text) {
      const panel = this.elements.panel;
      panel.textContent = '';
      panel.appendChild(createElement('div', { className: 'ab-search__message', text }));
      this.activeIndex = -1;
      this.open();
    }

    renderResults(query, results) {
      const panel = this.elements.panel;
      const products = (results.products || []).slice(0, PRODUCT_LIMIT);
      const queries = (results.queries || []).slice(0, QUERY_LIMIT);
      const collections = (results.collections || []).slice(0, COLLECTION_LIMIT);

      this.renderedQuery = query;
      this.activeIndex = -1;
      panel.textContent = '';

      if (!products.length && !queries.length && !collections.length) {
        const message = createElement('div', { className: 'ab-search__message' });
        message.appendChild(document.createTextNode('No matches for '));
        message.appendChild(createElement('strong', { text: `“${query}”` }));
        message.appendChild(document.createTextNode('. Try a flower type, colour or occasion.'));
        panel.appendChild(message);
        this.open();
        return;
      }

      if (queries.length) {
        panel.appendChild(this.buildQueryGroup(queries, query));
      }

      if (collections.length) {
        panel.appendChild(buildCollectionGroup(collections, query));
      }

      if (products.length) {
        panel.appendChild(this.buildProductGroup(products, query));
      }

      const footer = createElement('a', {
        className: 'ab-search__footer',
        text: `View all results for “${query}”`,
        attributes: {
          href: `${this.searchUrl()}?q=${encodeURIComponent(query)}&type=product`,
          'data-ab-search-option': '',
          'data-no-instant': '',
        },
      });
      panel.appendChild(footer);

      this.open();
    }

    buildQueryGroup(queries, term) {
      const group = createElement('div', { className: 'ab-search__group' });
      group.appendChild(createElement('p', { className: 'ab-search__group-title', text: 'Suggestions' }));

      const chips = createElement('div', { className: 'ab-search__chips' });

      queries.forEach((entry) => {
        const text = entry.text || '';
        const chip = createElement('a', {
          className: 'ab-search__chip',
          attributes: {
            href: entry.url || `${this.searchUrl()}?q=${encodeURIComponent(text)}&type=product`,
            'data-ab-search-option': '',
            'data-no-instant': '',
            role: 'option',
          },
        });
        chip.appendChild(highlightMatch(text, term));
        chips.appendChild(chip);
      });

      group.appendChild(chips);
      return group;
    }

    buildProductGroup(products, term) {
      const group = createElement('div', { className: 'ab-search__group' });
      group.appendChild(createElement('p', { className: 'ab-search__group-title', text: 'Products' }));

      const list = createElement('ul', { className: 'ab-search__list ab-search__products' });

      products.forEach((product) => {
        const option = createElement('a', {
          className: 'ab-search__option',
          attributes: {
            href: product.url,
            'data-ab-search-option': '',
            'data-no-instant': '',
            role: 'option',
          },
        });

        const imageUrl = product.featured_image && product.featured_image.url;
        if (imageUrl) {
          option.appendChild(
            createElement('img', {
              className: 'ab-search__thumb',
              attributes: {
                src: imageUrl.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2'),
                alt: '',
                width: '44',
                height: '44',
                loading: 'lazy',
                decoding: 'async',
              },
            })
          );
        } else {
          option.appendChild(createElement('span', { className: 'ab-search__thumb', attributes: { 'aria-hidden': 'true' } }));
        }

        const body = createElement('span', { className: 'ab-search__option-body' });

        const title = createElement('span', { className: 'ab-search__option-title' });
        title.appendChild(highlightMatch(product.title || '', term));
        body.appendChild(title);

        const price = this.buildPrice(product);
        if (price) body.appendChild(price);

        option.appendChild(body);

        const item = document.createElement('li');
        item.appendChild(option);
        list.appendChild(item);
      });

      group.appendChild(list);
      return group;
    }

    buildPrice(product) {
      const price = formatMoney(product.price);
      if (!price) return null;

      const meta = createElement('span', { className: 'ab-search__option-meta' });

      const varies = parseFloat(product.price_max) > parseFloat(product.price_min);
      meta.appendChild(document.createTextNode(varies ? `From ${price}` : price));

      const compare = parseFloat(product.compare_at_price_max || product.compare_at_price);
      if (isFinite(compare) && compare > parseFloat(product.price)) {
        meta.appendChild(createElement('s', { text: formatMoney(compare) }));
      }

      return meta;
    }

    open() {
      this.elements.panel.hidden = false;
      this.classList.add(STATE_CLASS.OPEN);
      this.elements.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      if (this.abortController) this.abortController.abort();
      this.setLoading(false);

      this.elements.panel.hidden = true;
      this.classList.remove(STATE_CLASS.OPEN);
      this.elements.input.setAttribute('aria-expanded', 'false');
      this.setActiveIndex(-1);
    }

    optionElements() {
      return Array.from(this.elements.panel.querySelectorAll(SELECTOR.option));
    }

    setActiveIndex(index) {
      const options = this.optionElements();
      options.forEach((option) => option.classList.remove(STATE_CLASS.ACTIVE));

      this.activeIndex = index;
      if (index < 0 || !options[index]) {
        this.elements.input.removeAttribute('aria-activedescendant');
        return;
      }

      const active = options[index];
      active.classList.add(STATE_CLASS.ACTIVE);
      if (!active.id) active.id = `ab-search-option-${Math.random().toString(36).slice(2, 8)}`;
      this.elements.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    syncClearButton() {
      if (!this.elements.clear) return;
      this.elements.clear.hidden = this.elements.input.value.length === 0;
    }
  }

  customElements.define('ab-search', AbSearch);

  // Results page: matching collections above the product grid.
  // The /search page can only list products, so collections come from predictive search.
  class AbSearchCollections extends HTMLElement {
    connectedCallback() {
      const term = (this.dataset.terms || '').trim();
      if (term.length < MIN_QUERY_LENGTH || this.loaded) return;
      this.loaded = true;

      const limit = Math.min(10, Math.max(1, parseInt(this.dataset.limit, 10) || 8));
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'collection',
        'resources[limit]': String(limit),
      });

      fetch(`${searchRoot()}/suggest.json?${params}`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((body) => {
          const collections = body && body.resources && body.resources.results ? body.resources.results.collections || [] : [];
          if (!collections.length) return;
          this.appendChild(
            buildCollectionGroup(collections, term, {
              className: 'ab-search-collections__group',
              titleClassName: 'ab-search-collections__title',
              chipsClassName: 'ab-search-collections__chips',
              chipClassName: 'ab-search-empty__chip',
              title: this.dataset.title || 'Collections',
              forPage: true,
            })
          );
          this.hidden = false;
        })
        .catch((error) => console.error('[ab-search-collections]', error));
    }
  }

  if (!customElements.get('ab-search-collections')) customElements.define('ab-search-collections', AbSearchCollections);
})();
